import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
export function ThreeBackground() {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, container.clientWidth / container.clientHeight, 0.1, 1000);
    camera.position.z = 5;
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true
    });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);
    // Gentle Floating Dust Motes
    // We create a few different particle systems to have varying sizes easily
    const sizes = [0.015, 0.025, 0.04];
    const counts = [30, 30, 20]; // Total 80 particles
    const particleSystems: {
      points: THREE.Points;
      geometry: THREE.BufferGeometry;
      material: THREE.PointsMaterial;
      velocities: Float32Array;
      offsets: Float32Array;
    }[] = [];
    sizes.forEach((size, index) => {
      const count = counts[index];
      const geometry = new THREE.BufferGeometry();
      const positions = new Float32Array(count * 3);
      const velocities = new Float32Array(count * 3);
      const offsets = new Float32Array(count); // For sine wave phase
      for (let i = 0; i < count; i++) {
        positions[i * 3] = (Math.random() - 0.5) * 14;
        positions[i * 3 + 1] = (Math.random() - 0.5) * 10;
        positions[i * 3 + 2] = (Math.random() - 0.5) * 8;
        velocities[i * 3] = (Math.random() - 0.5) * 0.001;
        velocities[i * 3 + 1] = Math.random() * 0.001 + 0.0005; // Slow upward drift
        velocities[i * 3 + 2] = (Math.random() - 0.5) * 0.001;
        offsets[i] = Math.random() * Math.PI * 2;
      }
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const material = new THREE.PointsMaterial({
        color: 0xc9953c,
        size: size,
        transparent: true,
        opacity: 0.25,
        sizeAttenuation: true
      });
      const points = new THREE.Points(geometry, material);
      scene.add(points);
      particleSystems.push({
        points,
        geometry,
        material,
        velocities,
        offsets
      });
    });
    let animationId: number;
    let time = 0;
    const animate = () => {
      animationId = requestAnimationFrame(animate);
      time += 0.01;
      particleSystems.forEach(({
        geometry,
        velocities,
        offsets
      }) => {
        const posArray = geometry.attributes.position.array as Float32Array;
        const count = posArray.length / 3;
        for (let i = 0; i < count; i++) {
          // Base drift
          posArray[i * 3] += velocities[i * 3];
          posArray[i * 3 + 1] += velocities[i * 3 + 1];
          posArray[i * 3 + 2] += velocities[i * 3 + 2];
          // Gentle sine wave motion on Y and X
          posArray[i * 3] += Math.sin(time * 0.5 + offsets[i]) * 0.002;
          posArray[i * 3 + 1] += Math.cos(time * 0.3 + offsets[i]) * 0.001;
          // Wrap around bounds
          if (posArray[i * 3 + 1] > 6) {
            posArray[i * 3 + 1] = -6;
            posArray[i * 3] = (Math.random() - 0.5) * 14;
          }
          if (posArray[i * 3] > 8) posArray[i * 3] = -8;
          if (posArray[i * 3] < -8) posArray[i * 3] = 8;
        }
        geometry.attributes.position.needsUpdate = true;
      });
      renderer.render(scene, camera);
    };
    animate();
    const handleResize = () => {
      if (!container) return;
      camera.aspect = container.clientWidth / container.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(container.clientWidth, container.clientHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
      particleSystems.forEach(({
        geometry,
        material
      }) => {
        geometry.dispose();
        material.dispose();
      });
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);
  return <div ref={containerRef} className="absolute inset-0 z-0 pointer-events-none" aria-hidden="true" data-id="element-40" />;
}